import React, { useState, useEffect, useMemo } from 'react';
import Modal from './Modal';
import { useUserSearch, useProfilePictureUrls } from '../hooks/useUserSearch';
import { getRecentUsers, getInitials } from '../utils/userProfiles';
import './UserSelectModal.css';

const UserSelectModal = ({ isOpen, onClose, onSelect, excludeUids = [] }) => {
  const { query, setQuery, results, searching, error, search } = useUserSearch();
  const [recentUsers, setRecentUsers] = useState([]);

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setRecentUsers(getRecentUsers());
    }
  }, [isOpen, setQuery]);

  // Before a search runs, offer recently viewed users
  const shownUsers = useMemo(
    () => (results ?? recentUsers).filter(user => !excludeUids.includes(user.uid)),
    [results, recentUsers, excludeUids]
  );
  const { pictureUrls, markPictureFailed } = useProfilePictureUrls(shownUsers);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      search();
    }
  };

  let emptyMessage = null;
  if (searching) {
    emptyMessage = 'Searching...';
  } else if (error) {
    emptyMessage = `Error: ${error}`;
  } else if (results === null && shownUsers.length === 0) {
    emptyMessage = 'Search by name, email, or UID and press Enter';
  } else if (results !== null && shownUsers.length === 0) {
    emptyMessage = `No users match "${query.trim()}"`;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Select User">
      <div className="user-select-modal">
        <input
          type="text"
          className="user-select-search"
          placeholder="Search by name, email, or UID, then press Enter"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
        />
        {results === null && shownUsers.length > 0 && (
          <div className="user-select-section-title">Recently viewed</div>
        )}
        <div className="user-list">
          {emptyMessage ? (
            <p className="empty-users">{emptyMessage}</p>
          ) : (
            shownUsers.map((user) => {
              const pictureUrl = pictureUrls.get(user.uid);
              return (
                <div
                  key={user.uid}
                  className="user-item"
                  onClick={() => onSelect(user)}
                >
                  <div className="user-select-avatar">
                    {pictureUrl ? (
                      <img
                        src={pictureUrl}
                        alt={user.name || 'User'}
                        className="user-select-profile-picture"
                        onError={() => markPictureFailed(user.uid)}
                      />
                    ) : (
                      <div className="user-select-initials">
                        {user.initials || getInitials(user.name)}
                      </div>
                    )}
                  </div>
                  <div className="user-info">
                    <h3>{user.name || user.uid}</h3>
                    <p className="user-email">{user.email || user.uid}</p>
                  </div>
                  <div className="user-select-arrow">→</div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </Modal>
  );
};

export default UserSelectModal;
